import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../useScreenInit.js';
import { Menu, X } from 'lucide-react';
// Import Pages
import { Home } from '../pages/Home';
import { CreatorWebsites } from '../pages/CreatorWebsites';
import { Hosting } from '../pages/Hosting';
import { Streaming } from '../pages/Streaming';
import { Pricing } from '../pages/Pricing';
import { Promotion } from '../pages/Promotion';
interface HipHopServersProps {
  onBack?: () => void;
}
export function HipHopServers({
  onBack
}: HipHopServersProps) {
  const screenInit = useScreenInit();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(screenInit?.activeSection || 'home');
  useEffect(() => {
    if (screenInit?.activeSection) {
      setActiveSection(screenInit.activeSection);
    }
  }, [screenInit]);
  const [isLive, setIsLive] = useState(true);
  const [auditQuery, setAuditQuery] = useState('');
  const [auditResult, setAuditResult] = useState<null | {
    score: number;
    issues: string[];
  }>(null);
  const runAudit = () => {
    if (!auditQuery.trim()) return;
    setAuditResult({
      score: Math.floor(Math.random() * 40) + 35,
      issues: ['No custom domain detected — using free subdomain', 'Social media links not centralized', 'No streaming/VOD infrastructure found', 'Missing press kit or EPK page', 'No direct monetization channels detected']
    });
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return <Home auditQuery={auditQuery} setAuditQuery={setAuditQuery} auditResult={auditResult} runAudit={runAudit} setAuditResult={setAuditResult} isLive={isLive} setIsLive={setIsLive} setActiveSection={setActiveSection} data-id="element-1" />;
      case 'websites':
        return <CreatorWebsites setActiveSection={setActiveSection} data-id="element-2" />;
      case 'hosting':
        return <Hosting setActiveSection={setActiveSection} data-id="element-3" />;
      case 'streaming':
        return <Streaming data-id="element-4" />;
      case 'pricing':
        return <Pricing data-id="element-5" />;
      case 'promotion':
        return <Promotion data-id="element-6" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-[#0D0D0D] text-white font-['Montserrat'] selection:bg-[#FFD700] selection:text-black" data-id="element-7">
      {/* Sticky Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0D0D0D]/80 backdrop-blur-xl border-b border-white/10 px-6 py-4" data-id="element-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-9">
          <div className="flex items-center gap-2" data-id="element-10">
            <img src="https://cdn.magicpatterns.com/uploads/iKAQFScHUv6u8yGPemC1Dw/Screenshot_2026-04-17_at_6.46.04_AM.png" alt="HipHopServers Logo" className="h-12 w-auto object-contain" data-id="element-11" />
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-widest" data-id="element-12">
            {['home', 'websites', 'hosting', 'streaming', 'pricing', 'promotion'].map(section => <button key={section} onClick={() => setActiveSection(section)} className={`${activeSection === section ? 'text-[#FFD700]' : 'text-white'} hover:text-[#FFD700] transition-colors`} data-id="element-13">
                {section.replace('-', ' ')}
              </button>)}
          </nav>
          <div className="flex items-center gap-4" data-id="element-14">
            {onBack && <button onClick={onBack} className="hidden lg:block text-xs font-bold uppercase tracking-widest hover:text-[#FFD700] transition-colors" data-id="element-15">
                ← Back to Admin
              </button>}
            <button className="hidden sm:block bg-[#FFD700] text-black px-6 py-2.5 rounded font-bold text-xs uppercase tracking-widest hover:bg-white transition-all shadow-[0_0_20px_rgba(255,215,0,0.3)]" data-id="element-16">
              Enter Dashboard
            </button>
            <button className="md:hidden text-white p-2" onClick={() => setIsMenuOpen(!isMenuOpen)} data-id="element-17">
              {isMenuOpen ? <X size={24} data-id="element-18" /> : <Menu size={24} data-id="element-19" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-20">
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} className="fixed inset-0 z-[60] bg-[#0D0D0D] p-8 flex flex-col gap-8" data-id="element-21">
            <div className="flex justify-end" data-id="element-22">
              <button onClick={() => setIsMenuOpen(false)} data-id="element-23">
                <X size={32} data-id="element-24" />
              </button>
            </div>
            <nav className="flex flex-col gap-6 text-2xl font-['Archivo_Black'] uppercase" data-id="element-25">
              {['home', 'websites', 'hosting', 'streaming', 'pricing', 'promotion'].map(section => <button key={section} onClick={() => {
            setActiveSection(section);
            setIsMenuOpen(false);
          }} className={activeSection === section ? 'text-[#FFD700]' : 'text-white'} data-id="element-26">
                  {section.replace('-', ' ')}
                </button>)}
            </nav>
          </motion.div>}
      </AnimatePresence>

      <main data-id="element-27">{renderContent()}</main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5" data-id="element-28">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-29">
          <img src="https://cdn.magicpatterns.com/uploads/iKAQFScHUv6u8yGPemC1Dw/Screenshot_2026-04-17_at_6.46.04_AM.png" alt="HipHopServers Logo" className="h-10 w-auto object-contain" data-id="element-30" />
          <p className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em]" data-id="element-31">
            Powered by enterprise-grade Niishcloud infrastructure
          </p>
          <div className="flex gap-6 text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-32">
            <a href="#" className="hover:text-white transition-colors" data-id="element-33">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-34">
              Terms
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-35">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>;
}